if (!customElements.get("m-product-tabs")) {
  class MProductTabs extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        loadMoreBtn: "[data-load-more-product]",
        productsContainer: "[data-products-container]",
        tabContent: [".m-tab-content"],
      };
      this.tabSliders = [];
      this.sliderEnabled = this.dataset.enableSlider === "true";
      this.mobileSliderDisable = this.dataset.mobileDisableSlider === "true";
      this.buttonType = this.dataset.buttonType;
      this.domNodes = queryDomNodes(this.selectors, this);
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = this.dataset.items;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      if (this.sliderEnabled) {
        for (let block of this.domNodes.tabContent) {
          this.initSliderByScreenSize(block);
          document.addEventListener("matchMobile", () => {
            this.initSliderByScreenSize(block);
          });
          document.addEventListener("unmatchMobile", () => {
            this.initSliderByScreenSize(block);
          });
        }
      }
      if (!this.sliderEnabled && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;

        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;
        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      document.addEventListener("matchMobile", () => {
        if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
          this.canLoad = true;
          this.currentPage = 1;
          for (let block of this.domNodes.tabContent) {
            this.initLoadMore(block);
          }
        }
      });
      this.initTabs();
      this.initMobileSelect();

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (event) => {
          const blockSelectedIsTab = event.target.classList.contains("m-tab-content");
          if (!blockSelectedIsTab) return;
          const dataIndex = event.target && event.target.dataset.index;
          this.tabs.setActiveTab(dataIndex);
        });
      }
    }

    initTabs() {
      this.tabs = new MinimogTheme.Tabs(this, (target) => {
        this.loadTab(target).then((tabContent) => {
          const slider = tabContent && tabContent.querySelector(".swiper-container");
          const controlsContainer = tabContent && tabContent.querySelector(".m-slider-controls");
          slider && slider.swiper && slider.swiper.update();
          const firstItem = slider && (slider.querySelector(".m-image") || slider.querySelector(".m-placeholder-svg"));
          if (firstItem && controlsContainer) {
            const itemHeight = firstItem.clientHeight;
            controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");
          }
        });
      });
    }

    async loadTab(target) {
      if (!target) return null;
      const tabContent = target.classList && target.classList.contains("m-tab-content")
        ? target
        : this.querySelector(target.getAttribute("href"));
      if (!tabContent || tabContent.dataset.loaded === "true") return tabContent;

      const handle = tabContent.dataset.collectionHandle;
      const limit = Math.min(Number(tabContent.dataset.productLimit || 10), 30);
      const productsContainer = tabContent.querySelector(this.selectors.productsContainer);
      if (!handle || !productsContainer || tabContent.dataset.loaded === "loading") return tabContent;

      tabContent.dataset.loaded = "loading";
      tabContent.setAttribute("aria-busy", "true");
      try {
        const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
        const response = await fetch(`${root}collections/${encodeURIComponent(handle)}/products.json?limit=${limit}`, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error(`Unable to load collection ${handle}`);
        const data = await response.json();
        const fragment = document.createDocumentFragment();
        (data.products || []).forEach((product) => fragment.append(this.createProductCard(product)));
        productsContainer.replaceChildren(fragment);
        tabContent.dataset.loaded = "true";
        if (this.sliderEnabled) this.initSliderByScreenSize(tabContent);
      } catch (error) {
        tabContent.dataset.loaded = "false";
      } finally {
        tabContent.removeAttribute("aria-busy");
      }
      return tabContent;
    }

    createProductCard(product) {
      const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/";
      const productUrl = `${root}products/${product.handle}`;
      const column = document.createElement("div");
      column.className = "swiper-slide m:column";
      const card = document.createElement("div");
      card.className = "m-product-card m-product-card--style-4 m:text-center";
      card.dataset.view = "card";
      card.dataset.productId = product.id;
      const media = document.createElement("div");
      media.className = "m-product-card__media";
      const imageLink = document.createElement("a");
      imageLink.className = "m-product-card__link m:block m:w-full";
      imageLink.href = productUrl;
      imageLink.setAttribute("aria-label", product.title);
      const imageData = product.images && product.images[0];
      if (imageData) {
        const imageWrapper = document.createElement("div");
        imageWrapper.className = "m-product-card__main-image";
        const responsiveImage = document.createElement("responsive-image");
        responsiveImage.className = "m-image";
        responsiveImage.style.setProperty("--aspect-ratio", "0.75");
        const image = document.createElement("img");
        image.src = this.resizeImage(imageData.src, 550);
        image.alt = imageData.alt || product.title;
        image.loading = "lazy";
        image.fetchPriority = "low";
        image.width = imageData.width || 550;
        image.height = imageData.height || 733;
        image.className = "m:w-full m:h-full";
        responsiveImage.append(image);
        imageWrapper.append(responsiveImage);
        imageLink.append(imageWrapper);
      }
      media.append(imageLink);
      const content = document.createElement("div");
      content.className = "m-product-card__content m:text-center";
      const info = document.createElement("div");
      info.className = "m-product-card__info";
      if (product.vendor) {
        const vendor = document.createElement("span");
        vendor.className = "m-product-card__vendor";
        vendor.textContent = product.vendor;
        info.append(vendor);
      }
      const heading = document.createElement("h3");
      heading.className = "m-product-card__title";
      const titleLink = document.createElement("a");
      titleLink.className = "m-product-card__name";
      titleLink.href = productUrl;
      titleLink.textContent = product.title;
      heading.append(titleLink);
      info.append(heading);
      const variants = product.variants || [];
      const prices = variants.map((variant) => this.priceToCents(variant.price)).filter(Number.isFinite);
      if (prices.length) {
        const priceWrapper = document.createElement("div");
        priceWrapper.className = "m-product-card__price";
        const price = document.createElement("p");
        price.className = "m-price";
        const minimum = Math.min(...prices);
        const prefix = prices.some((value) => value !== minimum) ? `${this.dataset.fromLabel} ` : "";
        price.textContent = `${prefix}${formatMoney(minimum, window.MinimogSettings.money_format)}`;
        priceWrapper.append(price);
        info.append(priceWrapper);
      }
      content.append(info);
      card.append(media, content);
      column.append(card);
      return column;
    }

    priceToCents(value) {
      const normalized = String(value == null ? "" : value).replace(/[^0-9.-]/g, "");
      if (!normalized) return NaN;
      return normalized.includes(".") ? Math.round(Number(normalized) * 100) : Number(normalized);
    }

    resizeImage(url, width) {
      if (!url) return "";
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}width=${width}`;
    }

    initSliderByScreenSize(sliderContainer) {
      const mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      const slider = sliderContainer.querySelector(".m-mixed-layout__wrapper");
      const controlsContainer = sliderContainer.querySelector(".m-slider-controls");

      if (MinimogTheme.config.mqlMobile && mobileDisableSlider) {
        controlsContainer && controlsContainer.classList.add("m:hidden");
        slider.classList.remove("swiper-container");
        if (slider.swiper) slider.swiper.destroy(false, true);
      } else {
        controlsContainer && controlsContainer.classList.remove("m:hidden");
        setTimeout(() => {
          this.initSlider(sliderContainer);
        });
      }
    }

    initSlider(sliderContainer) {
      const layoutWrapper = sliderContainer.querySelector(".m-product-list");
      const swiper = sliderContainer && sliderContainer.querySelector(".m-mixed-layout__wrapper");
      const controlsContainer = sliderContainer.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      const slideItemsLength =
        sliderContainer &&
        sliderContainer.querySelector(".swiper-wrapper") &&
        sliderContainer.querySelector(".swiper-wrapper").childElementCount;
      if (parseInt(this.items) >= slideItemsLength) {
        controlsContainer && controlsContainer.classList.add("m:hidden");
        layoutWrapper.classList.add("m-mixed-layout--mobile-grid");
        return;
      }

      swiper && swiper.classList.add("swiper-container");

      let slider = new MinimogLibs.Swiper(swiper, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: this.enableFlashsale ? false : true,
        pagination: this.showPagination
          ? {
              el: sliderContainer.querySelector(".swiper-pagination"),
              clickable: true,
            }
          : false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.items) >= 3 ? 3 : parseInt(this.items),
          },
          992: {
            slidesPerView: parseInt(this.items) >= 4 ? 4 : parseInt(this.items),
          },
          1280: {
            slidesPerView: parseInt(this.items),
          },
        },
        threshold: 2,
        on: {
          init: function () {
            setTimeout(() => {
              // Calculate controls position
              const firstItem =
                sliderContainer.querySelector(".m-image") || sliderContainer.querySelector(".m-placeholder-svg");
              if (firstItem && controlsContainer) {
                const itemHeight = firstItem.clientHeight;
                controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

                prevButton.classList.remove("m:hidden");
                nextButton.classList.remove("m:hidden");
              }
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            if (controlsContainer) {
              const { slidesPerView } = breakpointParams;
              if (slideItemsLength > slidesPerView) {
                controlsContainer.classList.remove("m:hidden");
                swiper.allowTouchMove = true;
              } else {
                controlsContainer.classList.add("m:hidden");
                swiper.allowTouchMove = false;
              }
            }
          },
        },
      });

      if (slider && this.showNavigation) {
        prevButton && prevButton.addEventListener("click", () => slider.slidePrev());
        nextButton && nextButton.addEventListener("click", () => slider.slideNext());
      }
    }

    initMobileSelect() {
      this.select = this.querySelector("[data-tab-select]");
      this.select.addEventListener("change", () => {
        this.tabs.setActiveTab(parseInt(this.select.value));
        this.loadTab(this.tabs.currentTab).then((tabContent) => {
          const slider = tabContent && tabContent.querySelector(".swiper-container");
          const controlsContainer = tabContent && tabContent.querySelector(".m-slider-controls");
          const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
          const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
          slider && slider.swiper && slider.swiper.update();
          const firstItem = slider && slider.querySelector(".m-image");
          if (firstItem && controlsContainer) {
            const itemHeight = firstItem.clientHeight;
            controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");
            prevButton && prevButton.classList.remove("m:hidden");
            nextButton && nextButton.classList.remove("m:hidden");
          }
        });
      });
    }

    initLoadMore(wrapper) {
      addEventDelegate({
        context: wrapper,
        selector: this.selectors.loadMoreBtn,
        handler: (e) => {
          e.preventDefault();
          this.handleLoadMore(wrapper);
        },
      });
    }

    handleLoadMore(wrapper) {
      const loadBtn = wrapper.querySelector(this.selectors.loadMoreBtn);
      const productsContainer = wrapper.querySelector(this.selectors.productsContainer);

      let currentPage = wrapper.dataset.page;
      currentPage = parseInt(currentPage);
      const totalPages = wrapper.dataset.totalPages;
      this.toggleLoading(loadBtn, true);

      const url = wrapper.dataset.url;
      const dataUrl = `${url}?page=${currentPage + 1}&section_id=${this.id}`;
      fetchCache(dataUrl).then((html) => {
        currentPage++;
        wrapper.dataset.page = currentPage;
        this.toggleLoading(loadBtn, false);
        const dom = generateDomFromString(html);
        const tabId = wrapper.getAttribute("id");
        const products = dom.querySelector(`#${tabId} ${this.selectors.productsContainer}`);

        if (products) {
          Array.from(products.childNodes).forEach((product) => productsContainer.appendChild(product));
        }

        if (currentPage >= parseInt(totalPages)) loadBtn && loadBtn.remove();
      });

      // Remove button focus
      loadBtn.blur();
    }

    toggleLoading(loadBtn, status) {
      if (!loadBtn) return;
      if (status) {
        loadBtn.classList.add("m-spinner-loading");
      } else {
        loadBtn.classList.remove("m-spinner-loading");
      }
    }
  }
  customElements.define("m-product-tabs", MProductTabs);
}
